import { observer } from 'mobx-react-lite'
import ModalState from 'reusable/mobx-helpers/ModalState'
import ShareDocumentDialogStore, { ShareDocumentDialogStoreProvider, ShareFromSource } from './ShareDocumentDialogStore'
import useStore from 'reusable/mobx-store-utils'
import { AdvisorClientContactDto } from '@gap/backend/app-services/advisor/client/contact/models/AdvisorClientContactDto'
import { useEffect } from 'react'
import { IosShareOutlined, PeopleOutlined } from '@mui/icons-material'
import { useOrgGuardStore } from 'features/main/main-router/org-guard/OrgGuardStore'
import ShareDocumentDialogInvite from './ShareDocumentDialogInvite'
import ShareDocumentDialogExport from './export/ShareDocumentDialogExport'
import AdvisorIntegrationFYIStore, { AdvisorIntegrationFYIStoreProvider } from 'features/org/edit/integrations/FYI/AdvisorIntegrationFYIStore'
import useInitialMount from 'reusable/hooks/useInitialMount'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import { DocShareDto } from '@gap/backend/app-services/doc-share/models/DocShareDto'

export interface ShareDocumentDialogProps {
   client: {
      advisorClientId: string
      contacts: AdvisorClientContactDto.minimal[]
      orgId: string
      name: string
      xeroId?: string
      fyiId?: string
   }
   modalState: ModalState
   documentAccessRights: DocShareDto.DocumentAccessRights[]
   document: DocShareDto.Document
   source?: ShareFromSource
   /** Called after an invite is successfully sent */
   onInviteSent?: () => void
}

export default observer(function ShareDocumentDialog(props: ShareDocumentDialogProps) {
   const orgGuardStore = useOrgGuardStore()
   const advisorIntegrationFYIStore = useStore(() => new AdvisorIntegrationFYIStore({ source: 'share-dialog', orgGuardStore }))

   const shareDocumentDialogStore = useStore(sp => new ShareDocumentDialogStore(sp), {
      ...props,
      orgGuardStore,
      source: props.source ?? 'Session finalisation'
   })

   useInitialMount(async () => {
      await Promise.allSettled([
         advisorIntegrationFYIStore.fetch({ xeroId: props.client.xeroId }),
         shareDocumentDialogStore.fetchClientAdvisorAccess()
      ])
   })

   useEffect(() => {
      shareDocumentDialogStore.updateContacts(props.client.contacts)
   }, [props.client.contacts, shareDocumentDialogStore])

   useEffect(() => {
      shareDocumentDialogStore.preselectFYIClient({
         clients: advisorIntegrationFYIStore.FYIClients,
         isLoadingClients: advisorIntegrationFYIStore.clientLoader.isLoading
      })
   }, [advisorIntegrationFYIStore.FYIClients, advisorIntegrationFYIStore.clientLoader.isLoading, props.client.name, props.client.xeroId, shareDocumentDialogStore])

   useEffect(() => {
      shareDocumentDialogStore.fields.selectedContactsField = []
   }, [shareDocumentDialogStore.fields, props.modalState.visible])

   return <ShareDocumentDialogStoreProvider store={shareDocumentDialogStore}>
      <AdvisorIntegrationFYIStoreProvider store={advisorIntegrationFYIStore}>
         <GapDialog
            size="small"
            title="Share this document"
            subTitle="Choose where to share this document"
            modalState={props.modalState}
            primary={shareDocumentDialogStore.primaryDialogButton}
            cancel={{
               name: 'Cancel',
               disabled: shareDocumentDialogStore.loader.isLoading
            }}
            tabs={[
               {
                  component: <ShareDocumentDialogInvite />,
                  startIcon: <PeopleOutlined fontSize='small' />,
                  label: 'Invite'
               },
               {
                  component: <ShareDocumentDialogExport />,
                  startIcon: <IosShareOutlined fontSize="small" />,
                  label: 'Export',
                  buttons: {
                     primary: {
                        name: 'Done'
                     }
                  }
               }
            ]}
         />
      </AdvisorIntegrationFYIStoreProvider>
   </ShareDocumentDialogStoreProvider>
})
